import { supabase } from './client';

/**
 * Configuration constants for profile photo uploads
 */
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_DIMENSION = 1024; // Max width/height in pixels
const QUALITY = 0.85; // JPEG quality (0-1)
const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

export interface UploadResult {
  url: string; // Full public URL for display
  path: string; // Storage path (e.g., "{tenantId}/{fileName}") for database storage
  error?: string;
}

/**
 * Compresses and resizes an image file to optimize storage and performance
 * @param file - The image file to compress
 * @returns Promise resolving to a compressed Blob
 */
function compressImage(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = event => {
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_DIMENSION) {
            height = (height / width) * MAX_DIMENSION;
            width = MAX_DIMENSION;
          }
        } else {
          if (height > MAX_DIMENSION) {
            width = (width / height) * MAX_DIMENSION;
            height = MAX_DIMENSION;
          }
        }

        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Use high-quality image rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to Blob with compression
        canvas.toBlob(
          blob => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }
            resolve(blob);
          },
          'image/jpeg', // Always convert to JPEG for better compression
          QUALITY
        );
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = event.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Validates file type and size before upload
 * @param file - The file to validate
 * @returns Error message if invalid, undefined if valid
 */
function validateFile(file: File): string | undefined {
  // Check file type
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return 'Invalid file type. Please upload a JPEG, PNG, or WebP image.';
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return `File size exceeds ${MAX_FILE_SIZE / (1024 * 1024)}MB. Please choose a smaller image.`;
  }

  return undefined;
}

/**
 * Uploads a profile photo to Supabase Storage with compression and validation
 * @param tenantId - The tenant ID for bucket organization
 * @param userId - The user ID for file naming
 * @param file - The image file to upload
 * @returns Promise resolving to upload result with URL or error
 */
export async function uploadProfilePhoto(
  tenantId: string,
  userId: string,
  file: File
): Promise<UploadResult> {
  try {
    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      return { url: '', path: '', error: validationError };
    }

    // Compress image
    let processedFile: Blob;
    try {
      processedFile = await compressImage(file);
    } catch (error) {
      return {
        url: '',
        path: '',
        error: error instanceof Error ? error.message : 'Failed to process image'
      };
    }

    // Check if compressed file is still too large (shouldn't happen, but safety check)
    if (processedFile.size > MAX_FILE_SIZE) {
      return {
        url: '',
        path: '',
        error: 'Image is too large even after compression. Please choose a smaller image.'
      };
    }

    // Generate unique filename
    const fileExt = 'jpg'; // Always use jpg after compression
    const fileName = `${userId}-${Date.now()}.${fileExt}`;
    // Note: filePath doesn't include bucket name, just the path within the bucket
    const filePath = `${tenantId}/${fileName}`;

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from('profile-photos')
      .upload(filePath, processedFile, {
        contentType: 'image/jpeg',
        cacheControl: '3600', // Cache for 1 hour
        upsert: false // Don't overwrite existing files
      });

    if (error) {
      // Handle specific storage errors
      if (error.message.includes('Bucket not found') || error.message.includes('does not exist')) {
        return {
          url: '',
          path: '',
          error:
            'Storage bucket "profile-photos" not found. Please create it in Supabase Dashboard under Storage > Buckets. See docs/setup/PROFILE_PHOTO_STORAGE_SETUP.md for instructions.'
        };
      }

      if (error.message.includes('already exists')) {
        // Try with a different timestamp
        const retryFileName = `${userId}-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const retryPath = `${tenantId}/${retryFileName}`;

        const { error: retryError } = await supabase.storage
          .from('profile-photos')
          .upload(retryPath, processedFile, {
            contentType: 'image/jpeg',
            cacheControl: '3600',
            upsert: false
          });

        if (retryError) {
          console.error('Retry upload error:', retryError);
          return {
            url: '',
            path: '',
            error: `Failed to upload image: ${retryError.message}. Please try again.`
          };
        }

        const { data: urlData } = supabase.storage.from('profile-photos').getPublicUrl(retryPath);

        return {
          url: urlData.publicUrl, // Full URL for display
          path: retryPath // Storage path for database
        };
      }

      console.error('Storage upload error:', error);
      return {
        url: '',
        path: '',
        error:
          error.message || 'Failed to upload image. Please check the browser console for details.'
      };
    }

    if (!data) {
      return {
        url: '',
        path: '',
        error: 'Upload failed. No data returned.'
      };
    }

    // Get public URL for display
    const { data: urlData } = supabase.storage.from('profile-photos').getPublicUrl(data.path);

    return {
      url: urlData.publicUrl, // Full URL for display
      path: data.path // Storage path for database
    };
  } catch (error) {
    return {
      url: '',
      path: '',
      error: error instanceof Error ? error.message : 'An unexpected error occurred during upload.'
    };
  }
}

/**
 * Deletes a profile photo from Supabase Storage
 * @param tenantId - The tenant ID
 * @param userId - The user ID
 * @param fileUrl - The URL of the file to delete
 * @returns Promise resolving to success boolean or error
 */
export async function deleteProfilePhoto(
  tenantId: string,
  userId: string,
  fileUrl: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Extract file path from URL
    // Handle both old format (with duplicate profile-photos) and new format
    const urlParts = fileUrl.split('/profile-photos/');
    if (urlParts.length < 2) {
      return { success: false, error: 'Invalid file URL' };
    }

    // Get the path after the last /profile-photos/ (handles old duplicate format)
    const pathAfterBucket = urlParts[urlParts.length - 1];
    // Remove tenantId prefix if present, then reconstruct with current tenantId
    const pathParts = pathAfterBucket.split('/');
    // If path has tenantId at start, remove it; otherwise use as-is
    const fileName = pathParts.length > 1 ? pathParts.slice(1).join('/') : pathParts[0];
    const filePath = `${tenantId}/${fileName}`;

    const { error } = await supabase.storage.from('profile-photos').remove([filePath]);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete image'
    };
  }
}
